/**
 * Em que altura o tocador de música toca.
 *
 * Não havia controle nenhum: o `<audio>` ficava no `volume = 1` de fábrica e
 * quem quisesse baixar mexia no volume do sistema inteiro. Num acervo assim
 * isso incomoda mais do que num tocador comum -- entre um 78 de 1928 e uma
 * coletânea de netlabel de agora há uma diferença de gravação que nenhuma
 * barra de sistema conserta, e ela vem no meio da fila.
 *
 * ── Um número só ──────────────────────────────────────────────────────────
 *
 * Mudo não é um segundo estado guardado: mudo é `volume === 0`. Guardar os
 * dois deixaria combinações que se contradizem -- "mudo, com volume em 0,7" e
 * "não mudo, com volume em zero" --, e a segunda é um botão de mudo que não
 * faz nada, porque tirar o mudo do zero devolve o zero. O nível de antes do
 * mudo vive na memória de quem chama, não aqui: é coisa de sessão, e voltar
 * amanhã é voltar de novo ao que ficou guardado.
 *
 * ── Só no aparelho ────────────────────────────────────────────────────────
 *
 * Como o `ondeParou.ts` ao lado: preferência de aparelho e não coisa do
 * casal. O volume bom no computador da sala não é o volume bom no fone.
 *
 * E como lá, o que volta do armazém é um texto que uma versão anterior do app
 * escreveu, ou que ficou pela metade porque o aparelho desligou no meio. A
 * diferença é que aqui não há `null` de recurso: sem nada de que se fiar,
 * volta-se ao som inteiro, que é onde o tocador sempre esteve.
 */

/** A chave no armazém do aparelho. */
export const CHAVE_VOLUME = 'musica_volume_v1';

/** O som inteiro: onde o `<audio>` já estava antes de existir este controle. */
export const VOLUME_PADRAO = 1;

/**
 * Para onde o mudo volta quando não há nível de antes.
 *
 * Só acontece num caso: alguém deixou o volume no zero, fechou o app, voltou e
 * apertou o mudo. Devolver o som *inteiro* nesse instante é um susto; metade é
 * o bastante para se ouvir e ajustar.
 */
export const VOLUME_MEIO = 0.5;

/** Apara para dentro de 0..1. O `<audio>` recusa qualquer coisa fora disso. */
export function limitar(v: number): number {
  if (!Number.isFinite(v)) return VOLUME_PADRAO;
  return Math.min(Math.max(v, 0), 1);
}

/**
 * O nível guardado, ou o som inteiro se não houver de que se fiar.
 *
 * Aceita `null` de entrada de propósito, como o `lerOndeParou`: é o que o
 * armazém devolve quando a chave não existe.
 *
 * O `parseFloat`, e não o `Number`: `Number(' ')` é zero, e um espaço em
 * branco no armazém abriria o app mudo sem nada na tela explicando por quê --
 * que é justamente a falha que mais custa caro num controle de volume.
 */
export function lerVolume(cru: string | null | undefined): number {
  if (!cru) return VOLUME_PADRAO;
  return limitar(Number.parseFloat(cru));
}

/** O texto a gravar. O par do `lerVolume`, e o motivo de viverem juntos. */
export function guardarVolume(v: number): string {
  return String(limitar(v));
}
